import React from 'react';
import PropTypes from 'prop-types';
import Bolt from '#/shared/components/GeneratedIcons/escooter/Bolt';
import Gruv from '#/shared/components/GeneratedIcons/escooter/Gruv';
import Lime from '#/shared/components/GeneratedIcons/escooter/Lime';
import LimeMultiColor from '#/shared/components/GeneratedIcons/escooter/LimeMultiColor';
import Razor from '#/shared/components/GeneratedIcons/escooter/Razor';
import Shared from '#/shared/components/GeneratedIcons/escooter/Shared';
import Biketown from '#/shared/components/GeneratedIcons/trimet/Biketown';
import Uber from '#/shared/components/GeneratedIcons/trimet/Uber';
import Lyft from '#/shared/components/GeneratedIcons/trimet/Lyft';
import ZipcarMultiColor from '#/shared/components/GeneratedIcons/trimet/ZipcarMultiColor';

const DEFAULT_ICON_KEY = 'default';
const TEXT_ICON_KEY = 'text';
const MULTI_COLOR_ICON_KEY = 'multiColor';

const biketownIcons = { [DEFAULT_ICON_KEY]: Biketown };
const gruvIcons = { [DEFAULT_ICON_KEY]: Gruv };

export const companyIconLookup = {
  BIKETOWN: biketownIcons,
  BOLT: { [DEFAULT_ICON_KEY]: Bolt },
  CLEVR: gruvIcons,
  GBFS: biketownIcons,
  GRUV: gruvIcons,
  LIME: {
    [DEFAULT_ICON_KEY]: Lime,
    [MULTI_COLOR_ICON_KEY]: LimeMultiColor,
  },
  LYFT: { [DEFAULT_ICON_KEY]: Lyft },
  RAZOR: { [DEFAULT_ICON_KEY]: Razor },
  SHARED: { [DEFAULT_ICON_KEY]: Shared },
  UBER: { [DEFAULT_ICON_KEY]: Uber },
  ZIPCAR: { [DEFAULT_ICON_KEY]: ZipcarMultiColor },
};

export default function RentalCompanyIcon({
  company,
  preferTextBased,
  preferMultiColor,
  ...svgProps
}) {
  const iconSet = companyIconLookup[company?.toUpperCase()] || {};
  const iconKey =
    (preferTextBased && TEXT_ICON_KEY) ||
    (preferMultiColor && MULTI_COLOR_ICON_KEY) ||
    DEFAULT_ICON_KEY;

  const Icon = iconSet[iconKey] || iconSet[DEFAULT_ICON_KEY];

  // prop spreading allows SVG props to be forwarded to the icon
  // eslint-disable-next-line react/jsx-props-no-spreading
  return Icon ? <Icon {...svgProps} /> : null;
}

RentalCompanyIcon.propTypes = {
  /** ID used to lookup company icon this component will display */
  company: PropTypes.string.isRequired,
  /** If `true` a text-based icon for company will be used if it exists */
  preferTextBased: PropTypes.bool,
  /** If `true` a multicolored icon for company will be used if it exists */
  preferMultiColor: PropTypes.bool,
};

RentalCompanyIcon.defaultProps = {
  preferTextBased: false,
  preferMultiColor: false,
};

/**
 * This higher-order component allows styles to be attached to the
 * `RentalCompanyIcon` before it's instantiated and is designed to allow a
 * styled version of the rental icon to be passed as a prop to otp-ui's
 * `TripOptions` component
 */
export const RentalCompanyIconWithStyles = ({
  preferTextBased = false,
  preferMultiColor = false,
  ...svgProps
}) => {
  function RentalCompanyIconWrapper({ company }) {
    return (
      <RentalCompanyIcon
        company={company}
        preferTextBased={preferTextBased}
        preferMultiColor={preferMultiColor}
        // spreading to follow pattern in main component
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...svgProps}
      />
    );
  }

  RentalCompanyIconWrapper.propTypes = {
    company: PropTypes.string.isRequired,
  };

  return RentalCompanyIconWrapper;
};
